import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@fpsm/cxy-components/Badge';
import { Button } from '@fpsm/cxy-components/Button';
import { Dialog as CxyDialog, type DialogProps } from '@fpsm/cxy-components/Dialog';

import { withLock } from '~/hocs/withLock';

import styles from './styles/Dialog.module.scss';

export const Trigger = React.forwardRef<
  HTMLButtonElement,
  { filtersCount?: number; isOpen: boolean; onClick: (isOpen: boolean) => void }
>(({ filtersCount, isOpen, onClick }, ref) => {
  const hasFilters = filtersCount !== undefined && filtersCount > 0;
  const { t } = useTranslation();
  return (
    <CxyDialog.Trigger
      aria-label="Toggle refinements Menu"
      className={styles.filtersButton}
      onClick={() => onClick(!isOpen)}
      ref={ref}
      size="xx-small"
      variant={hasFilters ? 'surface' : 'tertiary'}
    >
      {t('CX_PLUS_FILTERS')}
      {hasFilters && (
        <Badge className={styles.filtersCount} isRound size="s" variant="primary">
          {filtersCount}
        </Badge>
      )}
    </CxyDialog.Trigger>
  );
});

Trigger.displayName = 'DialogTrigger';

function DialogComponent({
  children,
  isDisabled,
  isOpen,
  onApply,
  setIsOpen,
  title,
  triggerRef,
  ...props
}: DialogProps & { isDisabled?: boolean; onApply?: () => void; ref?: React.Ref<HTMLButtonElement> }) {
  const { t } = useTranslation();
  // Filter out ref from props to avoid overwriting CxyDialog's internal dialogRef
  const { ref: _ref, ...dialogProps } = props;
  return (
    <CxyDialog
      className={styles.scroll}
      isFullScreen
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={title}
      triggerRef={triggerRef}
      {...dialogProps}
    >
      <div className={styles.dialogWrapper}>
        <div className={styles.content}>{children}</div>

        <div className={styles.footer}>
          <Button
            className={styles.applyButton}
            disabled={isDisabled}
            isFullWidth
            onClick={() => {
              onApply?.();
              setIsOpen(false);
            }}
            size="large"
            variant="secondary"
          >
            {t('CX_PLUS_APPLY')}
          </Button>
        </div>
      </div>
    </CxyDialog>
  );
}

export const DialogTrigger = withLock(Trigger, 'componentWall');
export const Dialog = withLock(DialogComponent, 'componentWall');
